import { isApprovalSlotUnassigned, type ApprovalLineMetaRow } from '@safetyhub/shared';
import type { ApprovalBlockData, ApprovalLineItem, ApprovalSignatureDisplay } from './types';

/** 빈 동적 슬롯 라벨 (고정 단계와 구분) */
const DYNAMIC_SLOT_LABEL = '추가 결재';

/**
 * 표본 서명 이미지(「서명」 텍스트 SVG) — fabrication `{라벨}` 관례의 이미지판
 * (결정 2026-09-04 자동연결 폐기 §2). 실물처럼 보이는 필기체가 아니라 **표본임이 자명한 표기**를
 * 쓴다 — 모사의 산출이지 실데이터 흉내가 아니다.
 */
const SAMPLE_SIGNATURE_SVG =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 60">' +
  '<text x="60" y="40" text-anchor="middle" font-size="26" font-family="serif" ' +
  'fill="#1d4ed8" transform="rotate(-6 60 30)">서명</text></svg>';

/**
 * 표본 서명 data URI — 순수 상수(base64 불요, `data:image/svg+xml;utf8,…` 계열).
 * 한글·`<>#` 등이 URI 어휘 밖이라 percent 인코딩으로 실는다(브라우저 문서 인코딩 무의존).
 * 소비처는 `<img src>`(resolveApprovalCellContent kind:'signature' → layout의 서명 이미지)다.
 */
export const SAMPLE_SIGNATURE_DATA_URI = `data:image/svg+xml;utf8,${encodeURIComponent(
  SAMPLE_SIGNATURE_SVG,
)}`;

/**
 * 템플릿 내장 결재라인 구조(approvalLineMeta) → 시뮬레이션용 ApprovalBlockData (P2a).
 *
 * 실제 보고서 데이터(서명/멤버) 없이, 템플릿이 정의한 결재라인 골격만으로 결재란을
 * 미리보기하기 위한 빈(서명 전) 결재 정의를 만든다.
 *
 * - `defaultApprovalLine`의 각 row → ApprovalLineItem(서명 없음). author 단계만 sourceType='author'.
 *   member/role 단계는 시뮬레이션이라 실제 멤버가 없으므로 이름을 비운다(label만 표시).
 * - 추가로 `maxAdditionalLines`개의 빈 동적 슬롯을 approvalLevel을 이어서 합성한다.
 *   한도는 **양식이 정의한 칸 위에 더 붙일 수 있는 수**이므로, 메타 칸(고정이든 편집 허용이든)이
 *   한도를 소비하지 않는다 — 그려야 할 빈 칸 수가 곧 한도다.
 *   동적 슬롯은 placeholder=true로 표식하여 layout이 고정 단계와 시각 구분한다.
 * - 서명이 없는 상태(approvalSignatures=[])이므로 모든 칸이 빈 칸으로 렌더된다.
 * - `options.signedLevel`(결재 서명 단계 모사 축, 2026-09-04): N이면 1..N 단계에 표본 서명
 *   (`SAMPLE_SIGNATURE_DATA_URI`)의 APPROVED 서명행을 합성한다 — buildMergedItems가 level로
 *   오버레이하고 resolveApprovalCellContent가 'signature'로 판정하는 실데이터와 동형.
 *   **기본(미지정)은 빈 결재란 그대로** — placeholder를 기본으로 깔면 "있는 척" 역방향 은닉
 *   (결정 정본). N이 칸 수를 넘으면 있는 칸까지만 채운다.
 */
export function buildSimulatedApprovalData(
  meta: { defaultApprovalLine: ApprovalLineMetaRow[]; maxAdditionalLines: number },
  options?: { signedLevel?: number },
): ApprovalBlockData {
  const defaultRows = meta.defaultApprovalLine ?? [];

  // 고정 단계: 메타 row → ApprovalLineItem (서명 없음)
  const fixedLines: ApprovalLineItem[] = defaultRows.map((row) => {
    // 체인 **머리**가 author면 작성자 칸 — 서버 resolve(`resolveAssignmentChain`)가 체인을 앞에서부터
    // 시도하고 author는 언제나 해소되므로 `[author, role]` 같은 다단 체인도 실폼에선 `sourceType:'author'`로
    // 온다. 그래서 단일 author 체인만 인정하는 shared `isAuthorStepDefinitionRow`(서버 preset ① — 상신 시
    // 즉시 확정 판정)를 쓰지 않는다 — 그건 「어느 칸이 작성 서명으로 채워지나」의 축이고, 여기는
    // 「어느 칸이 작성자 칸으로 그려지나」의 축이라 실폼 resolve와 같은 너비여야 캔버스 WYSIWYG가 지켜진다.
    const firstSource = row.assignmentChain?.[0];
    const isAuthor = firstSource?.type === 'author';
    return {
      // 시뮬레이션은 실제 결재자가 없으므로 신원(memberId) 없이 level만으로 식별한다
      approvalLevel: row.level,
      label: row.label,
      // author 단계만 sourceType 부여 — 그 외(role/member)는 시뮬레이션이라 미지정(이름 비움)
      sourceType: isAuthor ? 'author' : undefined,
      // 담당자 미지정 칸(빈 체인) — 실폼(web 어댑터)과 **같은 shared 술어**로 표식해야 캔버스가 이름 폴백
      // (member→**label**)을 타지 않는다. ApprovalLineMetaEditor의 새 칸 기본값이
      // 빈 체인이라, 이 표식이 없으면 Composer에서 만든 모든 칸이 캔버스에서만
      // 라벨("검토"·"결재")을 결재자 이름 자리에 띄워 WYSIWYG가 깨진다. 메타 행엔 memberId가 없으므로
      // 판정은 «체인이 비었는가»로 떨어진다.
      _assigneeUnset: isApprovalSlotUnassigned(row),
      _locked: row.locked,
    };
  });

  // 빈 동적 슬롯: `maxAdditionalLines`개를 고정 단계 다음 level부터 이어 붙인다.
  //
  // 한도는 **양식이 정의한 칸 외에 더 붙일 수 있는 칸 수**다 — 서버 판정도
  // `비-locked 총수 ≤ 양식 단독 비-locked 칸 수 + maxAdditionalLines`이므로(setReportApprovalLines
  // §5), 메타에 이미 있는 칸은 고정이든 편집 허용이든 한도를 쓰지 않는다. 따라서 한도를 그대로
  // 그리는 것이 실제로 붙일 수 있는 칸 수와 일치한다.
  //
  // ⚠️ 한때 "여유 = 한도 − 편집 허용 칸 수"로 깎았는데(2026-08-13 45f0b80c7), 그건 편집 허용 칸이
  // 한도를 먼저 소비하던 옛 판정(모델 B)에 맞춘 것이다. 그 판정 자체가 "양식 그대로도 저장 400"을
  // 내는 자기모순이라 폐기됐으므로, 깎으면 이제는 캔버스가 실제보다 **적게** 보여준다.
  const baseLevel = defaultRows.reduce((max, r) => Math.max(max, r.level), 0);
  const maxAdditional = Math.max(0, meta.maxAdditionalLines ?? 0);
  const dynamicLines: ApprovalLineItem[] = Array.from({ length: maxAdditional }).map((_, i) => ({
    approvalLevel: baseLevel + i + 1,
    label: DYNAMIC_SLOT_LABEL,
    // 동적 슬롯은 작성 시 추가 가능한 빈 칸 — 고정 단계와 시각 구분
    placeholder: true,
    // 아직 담당자가 없는 칸이므로 미지정. 없으면 이름 폴백이 라벨로 떨어져
    // StepperLayout의 이름란(`item.name || '-'`)에 '추가 결재'가 라벨과 중복 표시된다.
    _assigneeUnset: true,
  }));

  const approvalLines = [...fixedLines, ...dynamicLines];

  // 결재 서명 단계 모사(2026-09-04) — 1..N 단계에 표본 서명행 합성. 서명자 이름은 싣지 않는다:
  // 이름란은 정의(라인) 쪽 해소가 정본이고(_assigneeUnset 칸은 빈 이름 유지), 서명 모사가
  // 사람 이름까지 지어내면 fabrication 범위를 넘는다.
  const signedLevel = Math.max(0, Math.floor(options?.signedLevel ?? 0));
  const approvalSignatures: ApprovalSignatureDisplay[] =
    signedLevel > 0
      ? approvalLines
          .filter((line) => line.approvalLevel <= signedLevel)
          .map((line) => ({
            id: `sim-signed-${line.approvalLevel}`,
            approvalLevel: line.approvalLevel,
            status: 'APPROVED',
            signatureData: SAMPLE_SIGNATURE_DATA_URI,
          }))
      : [];

  return {
    approvalLines,
    approvalSignatures,
  };
}
